/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Sur une page collection, stocke l'URL + le nom court Shopify de la collection lors d'un clic sur un produit
if (window.location.pathname.startsWith('/collections/')) {
  document.querySelectorAll('a[href*="/products/"]').forEach(link => {
    link.addEventListener('click', function() {
      var breadcrumbColl = document.querySelector('.breadcrumb__item a[data-shopify-collection-title]');
      var collectionTitle = breadcrumbColl
        ? breadcrumbColl.getAttribute('data-shopify-collection-title')
        : document.title;
      sessionStorage.setItem('last_collection_url', window.location.pathname);
      sessionStorage.setItem('last_collection_title', collectionTitle);
    });
  });
}

// Sur une page produit, met à jour ou insère dynamiquement le breadcrumb collection depuis sessionStorage
if (window.location.pathname.startsWith('/products/')) {
  document.addEventListener('DOMContentLoaded', function() {
    const colUrl = sessionStorage.getItem('last_collection_url');
    const colTitle = sessionStorage.getItem('last_collection_title');
    const breadcrumbList = document.querySelector('.breadcrumb__list');
    const existingCollectionItem = document.querySelector('[data-breadcrumb-collection-item]');
    if (colUrl && colTitle && breadcrumbList) {
      if (existingCollectionItem) {
        // Met à jour le crumb si déjà présent
        const link = existingCollectionItem.querySelector('.breadcrumb__link');
        if (link) {
          link.href = colUrl;
          link.textContent = colTitle;
          link.setAttribute('data-shopify-collection-title', colTitle);
        }
      } else {
        // Sinon, le crée et l'insère juste après l'accueil
        const homeItem = breadcrumbList.querySelector('.breadcrumb__item:first-child');
        if (homeItem) {
          const newLi = document.createElement('li');
          newLi.className = 'breadcrumb__item';
          newLi.setAttribute('data-breadcrumb-collection-item', '');
          const newA = document.createElement('a');
          newA.className = 'breadcrumb__link';
          newA.href = colUrl;
          newA.textContent = colTitle;
          newA.setAttribute('data-shopify-collection-title', colTitle);
          newLi.appendChild(newA);
          homeItem.insertAdjacentElement('afterend', newLi);
        }
      }
    }
  });
}


// =================================================================================
// SCRIPT DE GESTION DE LA BARRE DE COMPARAISON STICKY (+ Drawer/Popover)
// =================================================================================

// --- INIT ---
document.addEventListener('DOMContentLoaded', initializeCompareScript);
document.addEventListener('shopify:section:load', initializeCompareScript);
document.addEventListener('sections:reloaded', initializeCompareScript);

// --- STORAGE ---
const STORAGE_KEY = 'shopifyProductCompareList';
const maxCompare = 3;

function getCompareList() {
  try {
    const list = localStorage.getItem(STORAGE_KEY);
    return list ? JSON.parse(list) : [];
  } catch {
    return [];
  }
}
function setCompareList(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch {}
}

// --- UI : checkboxes ---
function syncCheckboxesWithStorage() {
  const productObjectsInStorage = getCompareList();
  const productIdsInStorage = productObjectsInStorage.map(p => p.id);
  const checkboxesOnPage = document.querySelectorAll('.compare-checkbox');
  checkboxesOnPage.forEach(cb => {
    cb.checked = productIdsInStorage.includes(cb.dataset.productId);
  });
}

// --- UI : barre sticky ---
function refreshCompareBar() {
  const productObjectsInStorage = getCompareList();
  const currentCount = productObjectsInStorage.length;
  const compareBar = document.getElementById('compare-bar');
  const compareProductsContainer = document.getElementById('compare-products');
  const compareNowButtons = document.querySelectorAll('.compare-now-btn');

  if (compareBar) {
    currentCount > 0 ? compareBar.removeAttribute('hidden') : compareBar.setAttribute('hidden', '');
  }

  const buttonText = currentCount > 0 ? `Comparer (${currentCount}/${maxCompare})` : 'Comparer maintenant';
  compareNowButtons.forEach(btn => {
    btn.innerHTML = buttonText;
    btn.disabled = (currentCount < 2);
  });

  if (compareProductsContainer) {
    compareProductsContainer.innerHTML = '';
    productObjectsInStorage.forEach(product => {
      const stickyBlock = document.createElement('div');
      stickyBlock.className = 'product-sticky-form__content-block compare-sticky-block';
      stickyBlock.innerHTML = `
        <div class="product-sticky-form__image-wrapper">
          <img src="${product.imageSrc}" alt="${product.title}" class="product-sticky-form__image" loading="lazy">
        </div>
        <div class="product-sticky-form__info">
          <div class="product-sticky-form__bottom-info">
            <span class="product-sticky-form__title">${product.title}</span>
            <span class="square-separator square-separator--subdued"></span>
            <span class="product-sticky-form__price">${product.priceHtml || ''}</span>
          </div>
        </div>
        <button type="button" class="product-sticky-form__remove" data-remove-id="${product.id}" aria-label="Retirer ce produit" title="Retirer">✕</button>
      `;
      compareProductsContainer.appendChild(stickyBlock);
    });
  }

  // Désactivation des checkboxes si limite atteinte
  const allCheckboxes = document.querySelectorAll('.compare-checkbox');
  allCheckboxes.forEach(cb => {
    if (!cb.checked) {
      cb.disabled = (currentCount >= maxCompare);
    }
    const card = cb.closest('.product-item');
    if (card) {
      card.classList.toggle('product-item--compare', cb.checked);
    }
  });
}

// --- INIT GLOBALE ---
function initializeCompareScript() {
  syncCheckboxesWithStorage();
  refreshCompareBar();
  setupEventListeners();
}

// --- OUVERTURE drawer/popover ---
function openCompareUI() {
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const drawer  = document.getElementById('product-compare-drawer');
  const popover = document.getElementById('product-compare-popover');

  if (isMobile) {
    if (popover && !popover.hasAttribute('open')) popover.setAttribute('open','');
    document.documentElement.dispatchEvent(new CustomEvent('popover:open', { detail: { id: 'product-compare-popover' }}));
  } else {
    if (drawer && !drawer.hasAttribute('open')) drawer.setAttribute('open','');
    document.documentElement.dispatchEvent(new CustomEvent('drawer:open', { detail: { id: 'product-compare-drawer' }}));
  }
}

// --- RENDU drawer/popover ---
function renderCompareUIFromStorage() {
  const productDataList = getCompareList();
  const containers = document.querySelectorAll('.compare-container');
  if (!containers.length) return;

  if (!productDataList.length) {
    containers.forEach(c => c.innerHTML = '<p style="padding:1rem;">Aucun produit à comparer pour le moment.</p>');
    return;
  }

  const labelMap = {
    'poids': 'Poids','connectivite': 'Connectivité','dimensions_en_usage':'Dimensions en usage',
    'dimensions_plie':'Dimensions plié','inclinaison':'Inclinaison','indicateurs':'Indicateurs',
    'mesures_cardiaques':'Mesures cardiaques','pliable':'Pliable','poids_max_utilisateur':'Poids max utilisateur',
    'programmes':'Programmes','puissance_moteur':'Puissance moteur','surface_de_course':'Surface de course',
    'systeme_d_amortissement':'Système d\'amortissement','vitesses_max':'Vitesse max','roue_d_inertie':'Roue d\'inertie',
    'resistance':'Résistance','auto_alimente':'Auto-alimenté'
  };

  const attributesToDisplay = new Set();
  productDataList.forEach(p => {
    if (p && p.features) for (const k in p.features) attributesToDisplay.add(k);
  });

  let html = '<div class="compare-products-header">';
  productDataList.forEach(p => {
    html += `
      <div class="compare-products-header__cell">
        <img src="${p.imageSrc}" alt="${p.title}" class="compare-products-header__image" loading="lazy">
        <div class="compare-products-header__title">${p.title || ''}</div>
        <div class="compare-products-header__price">${p.priceHtml || ''}</div>
      </div>`;
  });
  html += '</div>';

  attributesToDisplay.forEach(key => {
    const label = labelMap[key] || key;
    html += `<div class="compare-feature-block">`;
    html += `<div class="compare-feature-block__header">${label}</div>`;
    html += `<div class="compare-feature-block__values">`;
    productDataList.forEach(p => {
      let value = '<span class="compare-table__no-value">-</span>';
      const raw = p?.features?.[key];
      if (raw != null) {
        if (typeof raw === 'object' && raw.value != null) {
          value = `${raw.value} ${raw.unit || ''}`.trim();
        } else {
          value = String(raw);
        }
      }
      html += `<div class="compare-feature-block__cell">${value}</div>`;
    });
    html += `</div></div>`;
  });

  containers.forEach(c => c.innerHTML = html);
}

// --- EVENTS ---
let eventListenersAttached = false;

function setupEventListeners() {
  if (eventListenersAttached) return;

  // Checkbox comparer
  document.body.addEventListener('change', (event) => {
    if (event.target.matches('.compare-checkbox')) handleCompareChange(event.target);
  });

  // Clics délégués
  document.body.addEventListener('click', (event) => {
    // Retirer un produit
    if (event.target.matches('.product-sticky-form__remove')) {
      event.preventDefault();
      handleRemoveClick(event.target);
      return;
    }

    // Ouvrir drawer/popover
    const openBtn = event.target.closest('.compare-now-btn');
    if (openBtn) {
      event.preventDefault();
      if (getCompareList().length >= 2) {
        openCompareUI();
        renderCompareUIFromStorage();
      }
      return;
    }

    // Tout effacer
    const clearBtn = event.target.closest('#compare-clear-btn');
    if (clearBtn) {
      event.preventDefault();
      setCompareList([]);
      refreshCompareBar();
      renderCompareUIFromStorage();
      syncCheckboxesWithStorage();

      // 🔥 Supprime aussi la classe CSS de sélection et reset les checkboxes
      document.querySelectorAll('.product-item').forEach(card => {
        card.classList.remove('product-item--compare');
        const cb = card.querySelector('.compare-checkbox');
        if (cb) {
          cb.checked = false;
          cb.disabled = false;
        }
      });
      return;
    }
  });

  // Sync multi-onglets
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      refreshCompareBar();
      renderCompareUIFromStorage();
    }
  });

  eventListenersAttached = true;
}

// --- HANDLERS ---
function handleCompareChange(checkbox) {
  const productId = checkbox.dataset.productId;
  const card = checkbox.closest('.product-item');
  let currentList = getCompareList();

  if (checkbox.checked) {
    if (currentList.length < maxCompare && !currentList.find(p => p.id === productId)) {
      const imgElement = card?.querySelector('.product-item__primary-image');
      currentList.push({
        id: productId,
        title: card?.querySelector('.product-item-meta__title')?.textContent.trim() || '',
        priceHtml: card?.querySelector('.product-item-meta__price-list-container')?.innerHTML || '',
        imageSrc: imgElement ? (imgElement.src || imgElement.dataset.src) : '',
        features: collectFeaturesData(card)
      });
    } else {
      if (currentList.length >= maxCompare) checkbox.checked = false;
    }
  } else {
    currentList = currentList.filter(p => p.id !== productId);
  }

  setCompareList(currentList);
  refreshCompareBar();
}

function handleRemoveClick(removeButton) {
  const idToRemove = removeButton.dataset.removeId;
  let currentList = getCompareList();
  currentList = currentList.filter(p => p.id !== idToRemove);
  setCompareList(currentList);
  initializeCompareScript();
  renderCompareUIFromStorage();
}

function collectFeaturesData(card) {
  if (card && card.dataset.featuresJson) {
    try {
      return JSON.parse(card.dataset.featuresJson);
    } catch (e) {
      console.error("Erreur parsing data-features-json:", e);
      return {};
    }
  }
  return {};
}

//====================================================================================
// --- FAV STORE SCRIPT DRAWER
//====================================================================================

(function(){
  // ===== Données magasins =====
  const HANDLE_TO={
    "67838968004":{name:"Decathlon Saint-Denis",slug:"decathlon-saint-denis"},
    "63769542852":{name:"Decathlon Saint-Pierre",slug:"decathlon-saint-pierre"},
    "67839033540":{name:"Decathlon Sainte-Suzanne",slug:"decathlon-sainte-suzanne"}
  };
  const ALL_HANDLES=Object.keys(HANDLE_TO);
  const SELECTOR_FORMS=['#fav-store-form-desktop','#fav-store-form-mobile'];

  let initialized=false, uiAttached=false;

  // ===== LS helper =====
  const LS={
    get:k=>{try{return localStorage.getItem(k)}catch(_){return null}},
    set:(k,v)=>{try{localStorage.setItem(k,v)}catch(_){}} 
  };

  // ===== Notification =====
  function ensureFavNotice(){
    let el=document.getElementById('fav-store-notice');
    if(!el){
      el=document.createElement('div');
      el.id='fav-store-notice';
      el.className='fav-store-notification';
      el.setAttribute('role','status');
      el.setAttribute('aria-live','polite');
      el.innerHTML='<div class="fav-store-notification__inner"><span id="fav-store-notice-text">Magasin favori enregistré</span><button type="button" class="fav-store-notification__close" aria-label="Fermer">×</button></div>';
      document.body.appendChild(el);
      el.querySelector('.fav-store-notification__close').addEventListener('click',hideFavNotice);
    }
    return el;
  }
  function showFavNotice(text,{duration=5000}={}){
    const el=ensureFavNotice();
    const txt=el.querySelector('#fav-store-notice-text');
    if(txt&&text) txt.textContent=text;
    el.classList.add('show');
    clearTimeout(el.__hideTimer);
    if(duration>0) el.__hideTimer=setTimeout(hideFavNotice,duration);
  }
  function hideFavNotice(){
    const el=document.getElementById('fav-store-notice');
    if(el) el.classList.remove('show');
  }

  // ===== Utils =====
  function handleize(s){
    return (s||'').toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
      .replace(/&/g,'and').replace(/[^a-z0-9]+/g,'-')
      .replace(/(^-|-$)/g,'').replace(/--+/g,'-');
  }
  async function updateCartAttributes(attrs){
    const res=await fetch('/cart/update.js',{
      method:'POST',
      headers:{'Content-Type':'application/json'},
      credentials:'same-origin',
      body:JSON.stringify({attributes:attrs})
    });
    if(!res.ok) throw new Error('Cart update failed');
    return res.json();
  }

  // ===== Core: set favorite (via clic bouton) =====
  async function setFavorite(handle){
    const meta=HANDLE_TO[handle]||{};
    const name=meta.name||'';
    const slug=meta.slug||handleize(name);
    try{
      LS.set('favorite_store_handle',handle);
      LS.set('favorite_store_name',name);
      LS.set('favorite_store_slug',slug);

      await updateCartAttributes({
        favorite_store_handle:handle,
        favorite_store_name:name,
        favorite_store_slug:slug
      });

      window.dispatchEvent(new Event('favorite-store:changed'));
      showFavNotice(`Magasin favori enregistré : ${name}`,{duration:5000});

      setTimeout(()=>{
        document.querySelector('#fav-store-drawer [data-action="close"]')?.click();
        document.querySelector('#fav-store-popover [data-action="close"]')?.click();
      },200);
    }catch(e){
      console.error('[FavStore] setFavorite error',e);
      const msgDesktop=document.getElementById('fav-store-msg-desktop');
      const msgMobile=document.getElementById('fav-store-msg-mobile');
      [msgDesktop,msgMobile].forEach(m=>{
        if(m){ m.textContent="Une erreur technique est survenue."; m.style.color="red"; }
      });
    }
  }

  // ===== UI helpers =====
  function markSelectionVisually(form,value){
    if(!form) return;
    const radio=form.querySelector(`input[type="radio"][name="fav_store"][value="${value}"]`);
    if(radio){
      radio.checked=true;
      form.querySelectorAll('.fav-store-card-content').forEach(d=>d.classList.remove('selected'));
      form.querySelectorAll('.fav-store-heart svg path').forEach(p=>p.setAttribute('fill','none'));
      radio.parentElement?.querySelector('.fav-store-card-content')?.classList.add('selected');
      const heart=radio.parentElement?.querySelector('.fav-store-heart svg path');
      if(heart) heart.setAttribute('fill','#3643BA');
      const btn=form.querySelector('.fav-store-choose-btn');
      if(btn) btn.disabled=false;
    }
  }
  function hydrateUI(){
    const handle=LS.get('favorite_store_handle');
    if(handle&&ALL_HANDLES.includes(handle)){
      SELECTOR_FORMS.forEach(sel=>{
        const form=document.querySelector(sel);
        markSelectionVisually(form,handle);
      });
    }
  }

  // ===== Listeners =====
  function attachUIHandlers(){
    if(uiAttached) return;
    uiAttached=true;

    document.addEventListener('change',e=>{
      if(e.target && e.target.matches('input[type="radio"][name="fav_store"]')){
        const radio=e.target;
        const form=radio.closest('form');
        markSelectionVisually(form,radio.value);
      }
    });

    document.addEventListener('click',e=>{
      const submitButton=e.target.closest('.fav-store-choose-btn');
      if(!submitButton) return;
      const form=submitButton.closest('form');
      if(!form) return;
      const selectedRadio=form.querySelector('input[type="radio"]:checked[name="fav_store"]');
      if(!selectedRadio){
        const msg=form.id.includes('desktop') ? document.getElementById('fav-store-msg-desktop') : document.getElementById('fav-store-msg-mobile');
        if(msg){ msg.textContent="Erreur : veuillez sélectionner un magasin."; msg.style.color="red"; }
        return;
      }
      submitButton.setAttribute('aria-busy','true');
      submitButton.disabled=true;
      setFavorite(selectedRadio.value).finally(()=>{
        submitButton.removeAttribute('aria-busy');
      });
    });

    // Nettoyage basique sur fermeture (si le thème crée un backdrop)
    document.addEventListener('click',e=>{
      const closeBtn=e.target.closest('#fav-store-drawer [data-action="close"], #fav-store-popover [data-action="close"], .modal-backdrop, [data-dismiss="modal"]');
      if(!closeBtn) return;
      const backdrop=document.querySelector('.modal-backdrop');
      if(backdrop && !backdrop.dataset.original) backdrop.remove();
      document.body.style.overflow='';
    },{capture:true});
  }

  // ===== Init =====
  function init(){
    if(initialized) return;
    initialized=true;
    hydrateUI();
    attachUIHandlers();
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',init);
  else init();

  // Debug minimal (utile pour support)
  window.favStoreDebug={
    checkElements:()=>{console.log('Drawer:',document.getElementById('fav-store-drawer'));console.log('Popover:',document.getElementById('fav-store-popover')); }
  };
})();


//====================================================================================================================================================
// REDIRECTION QR CODE BALISAGE
//====================================================================================================================================================

(function() {
 
  if (!window.location.pathname.startsWith('/qr/')) return;

  const match = window.location.pathname.match(/^\/qr\/(\d+)$/);
  if (!match) return;
  const code = match[1];

  
  document.body.innerHTML = `<style>@keyframes spin{to{transform:rotate(360deg)}}</style>
    <div style="position:fixed;top:0;left:0;width:100%;height:100%;background:#fff;display:flex;align-items:center;justify-content:center;z-index:9999">
      <div style="text-align:center">
        <div style="width:40px;height:40px;border:3px solid #ddd;border-top:3px solid #3743b3;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 15px"></div>
        <p style="margin:0;color:#666;font-family:sans-serif">Redirection...</p>
      </div>
    </div>`;

 
  fetch('https://redirection-yqzu.onrender.com/find-product?modelCode=' + encodeURIComponent(code))
    .then(res => res.json())
    .then(data => {
      if (data.url) {
        window.location.replace(data.url);
      } else {
        // Fallback si aucun produit trouvé
        window.location.replace('/collections/');
      }
    })
    .catch(() => {
      window.location.replace('/');
    });
})();


//===============================================================================================================================================
// Suppression cache pour éviter le bug des produits épuisés sur tout le site
//===============================================================================================================================================

(() => {
  const EXP = new Date('2025-10-01');            // fenêtre courte
  const LKEY = 'swCleanup_v2_done';              // flag persistant
  const SKEY = 'swCleanup_v2_session';           // flag session (anti-boucle)
  const RKEY = 'swCleanup_v2_reloadCount';       // compteur reload (sécurité)

  // 0) Garde-fous rapides
  if (new Date() > EXP) return;                  // expiré
  if (location.pathname.startsWith('/admin')) return;  // pas dans l'admin thème
  // Évite d'impacter les scans/headless
  const ua = navigator.userAgent || '';
  const isBot = /Headless|Lighthouse|bot|crawl|spider/i.test(ua) || navigator.webdriver;
  if (isBot) return;

  // 1) Storage helpers (Safari privé/quotas peuvent throw)
  const safeStorage = {
    getL(k){ try { return localStorage.getItem(k); } catch { return null; } },
    setL(k,v){ try { localStorage.setItem(k,v); return true; } catch { return false; } },
    getS(k){ try { return sessionStorage.getItem(k); } catch { return null; } },
    setS(k,v){ try { sessionStorage.setItem(k,v); return true; } catch { return false; } },
    removeL(k){ try { localStorage.removeItem(k); } catch {} }
  };

  // 2) Anti-répétition
  if (safeStorage.getL(LKEY) === '1' || safeStorage.getS(SKEY) === '1') return;

  // 3) Compteur reload anti-boucle
  const reloadCount = parseInt(safeStorage.getS(RKEY) || '0', 10);
  if (reloadCount >= 2) { // si 2 reloads d'affilée, on arrête
    safeStorage.setS(SKEY, '1');
    return;
  }

  // 4) Exécution asynchrone
  (async () => {
    try {
      const tasks = [];
      let swCount = 0, cacheNames = [];

      // A) Service Workers
      if ('serviceWorker' in navigator) {
        tasks.push(
          navigator.serviceWorker.getRegistrations()
            .then(regs => {
              swCount = regs.length;
              return Promise.allSettled(regs.map(r => r.unregister()));
            })
        );
      }

      // B) CacheStorage
      if (window.caches && caches.keys) {
        tasks.push(
          caches.keys().then(names => {
            cacheNames = names.slice();
            return Promise.allSettled(names.map(n => caches.delete(n)));
          })
        );
      }

      // C) Exécuter
      await Promise.allSettled(tasks);

      console.log(
        '✅ Cleanup SW/cache (auto):',
        { swUnregisterTried: swCount, cachesFound: cacheNames }
      );

      // 5) Flags + reload doux
      safeStorage.setL(LKEY, '1');           // ne rejouera plus pour ce navigateur
      safeStorage.setS(SKEY, '1');           // anti double exécution sur cette session
      safeStorage.setS(RKEY, String(reloadCount + 1));

      // reload sans revalidation de l'historique
      location.replace(location.href);
    } catch (e) {
      console.warn('Cleanup error:', e);
      // Éviter de retenter en boucle si exception inconnue
      safeStorage.setS(SKEY, '1');
    }
  })();
})();

//=========================================================================================
// progress bar product page sur mobile
//========================================================================================
(function () {
  function initLinearPager(pagerEl, flickityInstance) {
    var barEl = pagerEl.querySelector('.pager-progress__bar');
    if (!barEl) return;

    function updateBar() {
      var total = flickityInstance.slides.length;

      if (total <= 1) {
        // 1 seule image => barre pleine
        barEl.style.transform = 'scaleX(1)';
        return;
      }

      var index = flickityInstance.selectedIndex || 0;

      // ratio de progression entre 0 et 1
      // 0 = première image, 1 = dernière image
      var progressRatio = index / (total - 1);

      // Application via transform pour éviter le bug Safari sur width %
      barEl.style.transform = 'scaleX(' + progressRatio + ')';
    }

    // Position initiale
    updateBar();

    // Quand on change de slide
    flickityInstance.on('select', updateBar);
    flickityInstance.on('cellChange', updateBar);

    // Optionnel : si la taille du rail change (rotation écran, etc.)
    window.addEventListener('resize', updateBar);
  }

  function attachAllLinearPagers() {
    var pagerBlocks = document.querySelectorAll('.pager-progress');
    if (!pagerBlocks.length) return;

    pagerBlocks.forEach(function (pagerEl) {
      var controlsEl = pagerEl.closest('flickity-controls');
      if (!controlsEl) return;

      var carouselId = controlsEl.getAttribute('controls');
      if (!carouselId) return;

      var carouselEl = document.getElementById(carouselId);
      if (!carouselEl) return;

      var flickityRoot = carouselEl.closest('.flickity-enabled') || carouselEl;
      if (!flickityRoot) return;

      if (!window.ThemeFlickity || !window.ThemeFlickity.data) return;
      var instance = window.ThemeFlickity.data(flickityRoot);
      if (!instance) return;

      initLinearPager(pagerEl, instance);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    attachAllLinearPagers();
    // deuxième passe pour couvrir le cas où Flickity se monte juste après
    setTimeout(attachAllLinearPagers, 200);
  });
})();
